import { Column } from 'primereact/column';
import { UserSummaryType } from '../../../../../../../../api/types/user/user-summary.type';
import { DataTable } from 'primereact/datatable';
import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import globalCourseStore from '../../../../../../../../stores/global-course-store';
import {
  ClassSchedule,
  ExamSessionDetails,
} from '../../../../../../../../api/types/programs/course/exams/response/exam-metadata';
import { EditNumber } from '../../../../../../../../component/ui/edit-text/edit-number';
import { RequestEnrollment } from '../../../../../../../../api/types/programs/course/exams/requests/request-exam-enrollment-update';
import { EnrollmentWithUserDb } from '../../../../../../../../api/types/programs/course/exams/exam-sessions-enrollment-entity';
import { Button } from 'primereact/button';
import globalProgramStore from '../../../../../../../../stores/program-store';
import { upsertStudentsEnrollmentOperations } from '../../componenets/students-enrollment/operations/upsert-students-enrollment-operations';
import { approveGradesOperations } from './operations/approve-grades-operations';
import { InputSwitch } from 'primereact/inputswitch';

const columnStyle: CSSProperties = {
  textAlign: 'right',
  minWidth: '30px',
  maxWidth: '30px',
};

interface ExamPageGradesTabDatamodel {
  examId: number;
  examSession: ExamSessionDetails | null;
  setSessionDetails: (sessions: ExamSessionDetails[]) => any;
}

export const ExamPageGradesTab: React.FC<ExamPageGradesTabDatamodel> = ({
  examId,
  examSession,
  setSessionDetails,
}) => {
  const currentCourse = globalCourseStore.getCurrentCourse();
  const [loading, setLoading] = useState(false);

  const [studentClassRegistrations, setStudentClassRegistrations] = useState<
    Record<number, RequestEnrollment>
  >({});

  const isGradePopulated = Object.values(studentClassRegistrations).some(
    (registration) => registration.grade !== undefined,
  );

  const initialEnrollmentState = useRef<Record<number, RequestEnrollment>>({});

  const initializeStudentRegistrations = (
    examSession: ExamSessionDetails,
  ): Record<number, RequestEnrollment> => {
    const initialRegistrations: Record<number, RequestEnrollment> = {};

    examSession.classesSchedule.forEach((classSchedule: ClassSchedule) => {
      classSchedule.enrollment.forEach((enrollment: EnrollmentWithUserDb) => {
        initialRegistrations[enrollment.studentId] = {
          studentId: enrollment.studentId,
          scheduleId: enrollment.scheduleId,
          grade: enrollment.grade ?? undefined,
          appealingGrade: enrollment.appealingGrade ?? undefined,
        };
      });
    });

    return initialRegistrations;
  };

  useEffect(() => {
    if (examSession) {
      const initialState = initializeStudentRegistrations(examSession);
      initialEnrollmentState.current = initialState; // Store for comparison
      setStudentClassRegistrations(initialState); // Update current state
    }
  }, [examSession]);

  const hasChanges = useMemo(() => {
    const initialKeys = Object.keys(initialEnrollmentState.current);
    const currentKeys = Object.keys(studentClassRegistrations);

    if (initialKeys.length !== currentKeys.length) return true;

    for (const key of currentKeys) {
      const studentId = Number(key);
      const initial = initialEnrollmentState.current[studentId];
      const current = studentClassRegistrations[studentId];

      if (!initial) return true;

      if (
        initial.grade !== current.grade ||
        initial.appealingGrade !== current.appealingGrade ||
        initial.scheduleId !== current.scheduleId
      ) {
        return true;
      }
    }

    return false;
  }, [studentClassRegistrations]);

  const registerUsersEnrollment =
    examSession?.studentsRegistrations.filter((item) => item.enrollment) ?? [];
  const registerUsersEnrollmentSet = new Set(
    registerUsersEnrollment.map((item) => item.studentId),
  );

  const registerUser = currentCourse?.details?.students.filter((item) =>
    registerUsersEnrollmentSet.has(item.id),
  );

  const updateStudentGrade = (studentId: number, grade: number) => {
    setStudentClassRegistrations((prev) => {
      const newRegistrations = { ...prev };

      // If there's no existing registration for this student, create one
      if (!newRegistrations[studentId]) {
        newRegistrations[studentId] = {
          studentId,
          grade: grade,
        };
      } else {
        // Update existing registration
        newRegistrations[studentId] = {
          ...newRegistrations[studentId],
          grade: grade,
        };
      }

      return newRegistrations;
    });
  };

  const updateStudentAppealingGrade = (
    studentId: number,
    appealingGrade: number,
  ) => {
    setStudentClassRegistrations((prev) => {
      const newRegistrations = { ...prev };

      // If there's no existing registration for this student, create one
      if (!newRegistrations[studentId]) {
        newRegistrations[studentId] = {
          studentId,
          appealingGrade: appealingGrade,
        };
      } else {
        // Update existing registration
        newRegistrations[studentId] = {
          ...newRegistrations[studentId],
          appealingGrade: appealingGrade,
        };
      }

      return newRegistrations;
    });
  };

  const handleOnApprove = async () => {
    const currentProgramId = globalProgramStore.getCurrentId();

    await upsertStudentsEnrollmentOperations(
      currentProgramId,
      currentCourse?.metadata.id,
      examId,
      examSession?.id,
      initialEnrollmentState.current,
      studentClassRegistrations,
      setSessionDetails,
      setLoading,
    );
  };

  const [gradesApproval, setGradesApproval] = useState(
    examSession?.gradesApproval ?? false,
  );

  const handleApproveGrades = async (gradesApprovalCheck: boolean) => {
    const programId = globalProgramStore.getCurrentId();
    const currentCourseId = globalCourseStore.getCurrentCourse()?.metadata.id;

    await approveGradesOperations(
      programId,
      currentCourseId,
      examId,
      examSession?.id,
      gradesApprovalCheck,
      setGradesApproval,
      setSessionDetails,
    );
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <label>אישור ציונים</label>
        <InputSwitch
          checked={gradesApproval}
          disabled={!isGradePopulated}
          tooltip={
            isGradePopulated
              ? 'אישור זה יפרסם את הציונים לסטודנטים'
              : 'אישור ציוים ניתן רק אחר הזנת ציון אחד לפחות'
          }
          tooltipOptions={{
            showOnDisabled: true, // Only show tooltip when component is disabled
          }}
          onChange={(e) => handleApproveGrades(e.value)}
        />
      </div>
      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        selectionMode={'multiple'}
        multiple={true}
        groupRowsBy="dsf"
        value={registerUser}
        scrollable
        onSelectionChange={(e) => {
          //setSelectedUsers(e.value as UserSummaryType[]);
        }}
        dataKey="id"
        tableStyle={{
          direction: 'rtl',
          marginTop: '16px',
        }}
      >
        <Column
          expander={false}
          header="שם"
          field={'firstName'}
          style={columnStyle}
          body={(rowData: UserSummaryType) => (
            <label>
              {rowData.firstName} {rowData.lastName}
            </label>
          )}
        />

        <Column
          expander={false}
          header="ת.ז"
          field={'nationalId'}
          style={columnStyle}
        />

        <Column
          expander={false}
          header={'ציון'}
          field={'firstName'}
          style={columnStyle}
          body={(rowData: UserSummaryType) => (
            <div>
              <EditNumber
                min={0}
                max={100}
                value={studentClassRegistrations[rowData.id]?.grade}
                onChange={(value: number) =>
                  updateStudentGrade(rowData.id, value)
                }
              />
            </div>
          )}
        />

        <Column
          expander={false}
          header={'ציון לאחר ערעור'}
          field={'firstName'}
          style={columnStyle}
          body={(rowData: UserSummaryType) => (
            <div>
              <EditNumber
                min={0}
                max={100}
                disabled={
                  studentClassRegistrations[rowData.id]?.grade === undefined
                }
                value={studentClassRegistrations[rowData.id]?.appealingGrade}
                onChange={(value: number) =>
                  updateStudentAppealingGrade(rowData.id, value)
                }
              />
            </div>
          )}
        />
      </DataTable>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          marginTop: '16px',
        }}
      >
        <Button
          style={{ height: '30px' }}
          label={'אישור'}
          loading={loading}
          disabled={!hasChanges}
          tooltip={hasChanges ? 'יש לבצע שינוי בהזנת ציונים' : 'לחץ לאישור'}
          tooltipOptions={{ showOnDisabled: true }}
          onClick={handleOnApprove}
        />
      </div>
    </div>
  );
};
